"use client";

import { useState } from "react";
import { hooks, apijs, logjs } from "@lib/client";
import Consts from "@common/config/consts";
const log = new logjs("usePhotoDialog");
const api = new apijs("api/PhotoDialog");
const { useCustomDialog, useFormEx } = hooks;
import PhotoDialog from "./PhotoDialog";
const MAX_PHOTO_SLOTS = 10;

export const usePhotoDialog = (state, actions, params) => {
  const { IsLoading } = state;
  const { open, onClose, isOpen, renderDialog } = useCustomDialog(PhotoDialog);

  const [error, setError] = useState(null);
  const [photoData, setPhotoData] = useState({});
  const [images, setImages] = useState([]);
  const { mitsumoriNo, prid, seqnum, PartsName, index } = params;

  const initForm = {
    mitsumoriNo: mitsumoriNo,
    prid: prid,
    seqnum:seqnum,
    PartsName:PartsName,
  };

  const [form, formProps] = useFormEx(initForm, state, actions);

  /**
   * 写真一覧取得（Controller init）
   */
  const initParts = async () => {
    log.debug(`***************initParts START params`,params);
    log.debug(`***************initParts START ${initForm.mitsumoriNo} ${initForm.prid} ${initForm.seqnum}`);
    actions.showLoading?.();
    setError(null);

    try {
      const res = await api.post({ 
        mode: "initParts",
        mitsumoriNo: initForm.mitsumoriNo,
        prid: initForm.prid || 0,
        seqnum: initForm.seqnum,
        revision: "edit" 
      });
      log.debug("***************initParts END", res);

      if (res?.result) {
        setPhotoData(res.data);
        const corpId = res.data.request.CorpID;

        // 画像一覧の初期化
        const photos = Array.from({ length: MAX_PHOTO_SLOTS }, (_, i) => {
          const seq = i + 1;
          const hit = res.data.partsPict?.find((p) => p.Seqnum === seq);

          if (!hit) {
            return { seq, url: "/images/noimage.png", canDelete: false, originalname: "" };
          }

          // 🔽 優先的に S3の署名付きURL(hit.url) を使用
          const imageUrl = `${state?.initData?.S3ImageFolderDirect}/${hit.PictPath}?v=${Date.now()}`;

          return {
            seq,
            url: imageUrl,
            canDelete: true,
            originalname: hit.originalname || "",
            pictPath: hit.PictPath, // 保存用には相対パスでもOK
          };
        });

        setImages(photos);
        log.debug("initParts END photos", photos);
      }
    } catch (error) {
      log.error("initParts error", error);
    }finally {
      actions.hideLoading();
    }
  };

  /**
   * ファイル検証
   */
  const validateFile = (file) => {
    const okType = /image\/(jpeg|png)/i.test(file.type);
    if (!okType) return "JPEGまたはPNGファイルを指定してください";
    if (file.size > 4000000) return "アップロードできるファイルサイズは、4MBまでとなります";
    return null;
  };

  /**
   * 単一画像アップロード（Controller upload）
   */
  const uploadPhoto = async (seq, imageFiles) => {
    log.debug("uploadPhoto START", { seq, imageFiles: imageFiles?.length });

    if (!imageFiles || imageFiles.length === 0) return;

    actions.showLoading();
    try {
      const res = await api.post({
        mode: "uploadParts",
        mitsumoriNo: initForm.mitsumoriNo,
        seqnum: seq,
        prid: initForm.prid || 0,
        seqnumParts: initForm.seqnum,
        files: imageFiles,
      });

      log.debug("uploadPhoto END", res);
      if (res?.result) {
        await initParts(); // 一覧再取得
        actions.showSuccess("完了", "アップロードしました");
      } else {
        actions.showError("エラー", res?.error || "アップロードに失敗しました");
      }
      return res;
    } catch (e) {
      log.error("uploadPhoto error", e);
      actions.showError("エラー", e.message);
      return { result: false, error: e.message };
    } finally {
      actions.hideLoading();
    }
  };

  /**
   * 一括アップロード（Controller upload with seqnum=99）
   */
  const uploadPhotoBulk = async (imageFiles) => {
    log.debug("uploadPhotoBulk START", { imageFiles: imageFiles?.length });

    if (!imageFiles || imageFiles.length === 0) return;

    actions.showLoading();
    try {
      const _params = await api.post({
        mode: "uploadParts",
        mitsumoriNo: initForm.mitsumoriNo,
        seqnum: 99, // 一括アップロード
        prid: initForm.prid || 0,
        seqnumParts: initForm.seqnum,
        files: imageFiles,
      });
      const result = await api.post(_params);

      log.debug("uploadPhotoBulk END", result);
      if (result?.result) {
        await initParts(); // 一覧再取得
        actions.showSuccess("完了", "一括アップロードしました");
      } else {
        actions.showError("エラー", result?.error || "一括アップロードに失敗しました");
      }
      return result;
    } catch (e) {
      log.error("uploadPhotoBulk error", e);
      actions.showError("エラー", e.message);
      return { result: false, error: e.message };
    } finally {
      actions.hideLoading();
    }
  };

  /**
   * 画像削除（Controller deleteImage）
   */
  const deletePhoto = async (seq) => {
    const ok = await actions.showConfirm("画像を削除しますか？");
    if (!ok) return { result: false };

    actions.showLoading();
    try {
      const res = await api.post({
        mode: "deleteImage",
        mitsumoriNo: initForm.mitsumoriNo,
        seqnum: seq,
        prid: initForm.prid || 0,
        seqnumParts: initForm.seqnum,
      });

      if (res?.result) {
        await initParts(); // 一覧再取得
        actions.showSuccess("完了", "削除しました");
      } else {
        actions.showError("エラー", res?.error || "削除に失敗しました");
      }
      return res;
    } catch (e) {
      log.error("deletePhoto error", e);
      actions.showError("エラー", e.message);
      return { result: false, error: e.message };
    } finally {
      actions.hideLoading();
    }
  };
  
  /**
   * 一括画像削除（Controller deleteImage）
   */
  const deleteAllImages= async (seq) => {
    log.debug("deleteAllImages START", form);
    actions.showLoading();
    try {
    const _params = {
      mode: "deleteAllImages",
      mitsumoriNo: initForm.mitsumoriNo,
      prid: initForm.prid || 0,
      seqnum: initForm.seqnum,
    };
      const result = await api.post(_params);
      log.debug("deleteAllImages END", result);
      return result;
    } catch (error) {
      log.error("deleteAllImages error:", error);
      return { error: error.message, result: false };
    } finally {
      actions.hideLoading();
    }
  }

  /**
   * 備考更新（Controller update_bikou）
   */
  const updateBikou = async () => {
    log.debug("updateBikou START");
    actions.showLoading();

    try {
      const res = await api.post({
        mode: "update_bikou",
        mitsumoriNo: initForm.mitsumoriNo,
        bikou: initForm.bikou,
      });

      if (res?.result) {
        actions.showSuccess("完了", "備考を更新しました");
      } else {
        actions.showError("エラー", res?.error || "備考の更新に失敗しました");
      }
      return res;
    } catch (e) {
      log.error("updateBikou error", e);
      actions.showError("エラー", e.message);
      return { result: false, error: e.message };
    } finally {
      actions.hideLoading?.();
    }
  };

  return [
    { form, IsLoading, error, photoData, images, mitsumoriNo },
    {
      ...formProps,
      isOpen,
      renderDialog,
      open,
      onClose,
      initParts,
      uploadPhoto,
      uploadPhotoBulk,
      deletePhoto,
      deleteAllImages,
      updateBikou,
      setImages,
    },
  ];
};

export default usePhotoDialog;
